/**
 * „Twój plan" — the slab that stands beside the Subskrypcja tab.
 *
 * It says three things and sells nothing: the plan's name, how many clients it carries, and — on a
 * paid plan — the crown plaque the app draws for it. There is no price and no upgrade button here,
 * because there is no checkout on the web and an offer nobody can take is an advertisement.
 *
 * ── The free plan renders WITHOUT the plaque ────────────────────────────────────────────────
 * Not a greyed-out one and not a placeholder: the crown is what a paid plan has, so its absence is
 * the honest drawing of a free one. The plate keeps its own shape either way — the text block below
 * is what it is made of, and the image is what sits on top when there is one.
 *
 * ── It is dark in both themes ───────────────────────────────────────────────────────────────
 * Which makes its ink theme-independent too: `--plate-text*`, from `tokens/colors.css`. `var(--text)`
 * here would be near-black on Flame and the plate's own name would disappear.
 */

import { useTranslation } from 'react-i18next';

import photoBox from '~/shared/ui/photoBox.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { AccountSubscriptionApi } from '../lib/useSubscriptionCard';
import screen from './AccountScreen.module.css';
import styles from './PlanPlate.module.css';

interface PlanPlateProps {
  readonly subscription: AccountSubscriptionApi;
}

export function PlanPlate({ subscription }: PlanPlateProps) {
  const { t } = useTranslation();
  const { card, waiting, skeleton, reveal } = subscription;
  // `card` is `null` for the whole gated wait, not only while the read is open — see
  // `useSubscriptionCard`. So the plaque and the two lines hand over together.

  /**
   * The plaque is a paid plan's, and the square it sits in belongs to the PLATE.
   *
   * That is what keeps this object still. The plaque cannot be drawn until the read says which plan
   * this is, and a plate that grew by a square when the answer landed would move under the pointer
   * — so the square is always there, part of the slab, and what changes is whether there is a
   * picture in it. A free plan is then a plain dark plate rather than a plate with a hole in it.
   */
  const crowned = card !== null && !card.isFree;

  return (
    <div className={screen.sidePanel}>
      {/* The section's WAIT, not its read: the two bars below keep the gate's 300ms minimum life
        * after the answer lands, and a plate still drawing them is still about to change. Owner,
        * 21 September 2026 — the attribute and the hidden sentence are the pair, on one value. */}
      <div className={styles.plate} aria-busy={waiting}>
        <div className={styles.imageBox}>
          {crowned && (
            <img
              className={`${String(photoBox.photo)} ${String(styles.image)} ${String(reveal)}`}
              src="/account/plan-crown.jpg"
              alt=""
              decoding="async"
            />
          )}
        </div>

        <div className={styles.text}>
          <h2 className={styles.eyebrow}>{t('web:account.subscription.plateEyebrow')}</h2>

          {/* The two lines are ALWAYS the paragraphs, and the wait is a bar INSIDE them.
            * A bar standing in place of a paragraph is a box of its own height — three pixels short
            * on one line and four on the other — and the plate is centred in its column, so the
            * difference moved the whole slab by five when the answer landed. Measured on a cold
            * entry with 900ms of latency injected; the line box is the reserve. */}
          <p className={styles.name}>
            {skeleton ? (
              <span className={`${String(skel.bar)} ${String(styles.nameBar)}`} />
            ) : (
              <span className={String(reveal)}>{card?.planName ?? '—'}</span>
            )}
          </p>

          <p className={styles.seats}>
            {skeleton ? (
              <span className={`${String(skel.bar)} ${String(styles.seatsBar)}`} />
            ) : (
              <span className={String(reveal)}>{card?.plateSeats ?? ''}</span>
            )}
          </p>

          {waiting && (
            <span className={hidden.srOnly}>{t('web:account.subscription.waiting')}</span>
          )}
        </div>
      </div>
    </div>
  );
}
